import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { FileText, CheckCircle2, Loader2, MapPin, Phone } from "lucide-react";
import { apiClient, API, formatApiError } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";
import logo from "@/assets/logo.png";
import { toast } from "sonner";

export default function SignContract() {
  const { token } = useParams();
  const [contract, setContract] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [signatureImage, setSignatureImage] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/contracts/sign/${token}`)
      .then(({ data }) => setContract(data))
      .catch(() => setNotFound(true));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!signerName.trim()) return toast.error("Please type your full name");
    if (!signatureImage) return toast.error("Please draw your signature");
    if (!agreed) return toast.error("Please confirm you agree to the terms");

    setSubmitting(true);
    try {
      const { data } = await apiClient.post(`/contracts/sign/${token}`, {
        signer_name: signerName.trim(),
        signature_image: signatureImage,
      });
      setContract(data);
      toast.success("Agreement signed successfully");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not submit signature");
    } finally {
      setSubmitting(false);
    }
  };

  if (notFound) {
    return (
      <PageShell>
        <p className="text-ink-soft" data-testid="sign-contract-not-found">
          This agreement link could not be found. Please check the link or contact Ferguson's for a new one.
        </p>
      </PageShell>
    );
  }

  if (!contract) {
    return (
      <PageShell>
        <Loader2 className="animate-spin text-gold mx-auto" size={28} />
      </PageShell>
    );
  }

  const pdfUrl = `${API}/contracts/sign/${token}/pdf`;

  return (
    <PageShell>
      {contract.signature_status === "signed" ? (
        <div className="text-center" data-testid="sign-contract-already-signed">
          <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-3" />
          <h2 className="font-display font-bold text-xl text-ink">Agreement Signed</h2>
          <p className="text-ink-soft mt-2">
            Signed by <strong>{contract.signer_name}</strong> on {new Date(contract.signed_at).toLocaleDateString()}.
          </p>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="sign-contract-download-signed-pdf"
            className="inline-flex items-center gap-2 mt-5 bg-gold hover:bg-gold-hover text-[#0A140F] font-semibold px-5 py-2.5 rounded-md transition-colors"
          >
            <FileText size={15} /> View Signed Agreement
          </a>
        </div>
      ) : (
        <>
          <h2 className="font-display font-bold text-xl text-ink text-center">Service Agreement Review &amp; Signature</h2>
          <p className="text-ink-soft text-center mt-1 text-sm">
            Prepared by Ferguson's Customs Broker &amp; Freight Forwarders Limited for {contract.client_name}
          </p>

          <div className="mt-6 rounded-xl border border-ink/10 bg-cream-alt p-4 text-sm space-y-1.5" data-testid="sign-contract-summary">
            <SummaryRow label="Service Type" value={contract.service_type} />
            <SummaryRow label="Trade Lane" value={contract.trade_lane} />
            <SummaryRow label="Shipment Frequency" value={contract.shipment_frequency} />
            <SummaryRow label="Agreed Rates" value={contract.agreed_rates} />
            <SummaryRow label="Payment Terms" value={contract.payment_terms} />
            {contract.special_conditions && contract.special_conditions !== "None specified" && (
              <SummaryRow label="Special Conditions" value={contract.special_conditions} />
            )}
          </div>

          <a
            href={pdfUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="sign-contract-view-pdf"
            className="inline-flex items-center gap-2 mt-4 text-sm font-medium text-gold-muted hover:text-gold"
          >
            <FileText size={14} /> View Full Agreement (PDF)
          </a>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="sign-contract-form">
            <div>
              <label className="text-xs text-ink-faint uppercase tracking-wide mb-1 block">Printed Full Name</label>
              <input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                data-testid="sign-contract-name-input"
                className="w-full border border-ink/15 rounded-md px-3 py-2 text-sm bg-white"
              />
            </div>

            <div>
              <label className="text-xs text-ink-faint uppercase tracking-wide mb-1 block">Signature</label>
              <SignaturePad onChange={setSignatureImage} />
            </div>

            <label className="flex items-start gap-2 text-sm text-ink-soft cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                data-testid="sign-contract-agree-checkbox"
                className="mt-0.5 accent-gold"
              />
              I have reviewed the agreement above and agree to its terms.
            </label>

            <button
              type="submit"
              disabled={submitting}
              data-testid="sign-contract-submit-btn"
              className="w-full bg-gold hover:bg-gold-hover disabled:opacity-50 text-[#0A140F] font-display font-semibold px-5 py-2.5 rounded-md transition-colors"
            >
              {submitting ? "Submitting..." : "Sign Agreement"}
            </button>
          </form>
        </>
      )}
    </PageShell>
  );
}

const SummaryRow = ({ label, value }) => (
  <div className="flex gap-2">
    <span className="font-semibold text-ink shrink-0">{label}:</span>
    <span className="text-ink-soft">{value}</span>
  </div>
);

const PageShell = ({ children }) => (
  <div className="min-h-screen bg-cream flex items-center justify-center px-4 py-12" data-testid="sign-contract-page">
    <div className="w-full max-w-xl bg-white rounded-2xl border border-ink/10 shadow-lg p-6 sm:p-8">
      <img src={logo} alt="Ferguson's" className="h-12 w-auto mx-auto mb-5" />
      {children}

      <div className="mt-8 pt-6 border-t border-ink/10" data-testid="sign-contract-footer">
        <p className="text-xs text-ink-faint uppercase tracking-wide text-center mb-3">
          Questions about this agreement?
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-sm text-ink-soft">
          <span className="flex items-center gap-2" data-testid="sign-contract-footer-address">
            <MapPin size={14} className="text-gold-muted shrink-0" />
            115B Red Hills Road, Kingston 19, Jamaica
          </span>
          <span className="flex items-center gap-2" data-testid="sign-contract-footer-phone">
            <Phone size={14} className="text-gold-muted shrink-0" />
            <a href="tel:+18763958332" className="hover:text-gold-muted transition-colors">876-395-8332</a>
          </span>
        </div>
        <p className="text-center text-xs text-ink-faint mt-4">
          © {new Date().getFullYear()} Ferguson's Customs Broker &amp; Freight Forwarders Limited
        </p>
      </div>
    </div>
  </div>
);
